/**
 * Opções do filtro de categoria, derivadas dos parceiros já carregados.
 *
 * Antes isto era uma requisição a `/productor/digital_products/:id/partner_categories`
 * — o namespace do produtor. Participante recebe 403 ali, então o filtro nunca
 * funcionou para quem usa esta tela; parecia funcionar apenas para contas com
 * papel administrativo, que é o perfil de quem costuma testar.
 *
 * Derivar é mais que a correção barata: a lista de opções só deveria conter
 * categorias que de fato aparecem na tela, e essas são exatamente as dos
 * parceiros carregados. Buscar a lista completa oferecia filtros que não
 * filtram nada.
 *
 * Ver a change openspec `gamification-client-derived-state`, grupo 4.
 */

type PartnerCategory = { id?: number | string | null; name?: string | null }

/**
 * O Parceiro passou a ocupar N categorias: a api entrega
 * `partner_categories: PartnerCategory[]`, e `partner_category` saiu do
 * payload. A lista vem resolvida por `Partner#partner_categories`, que faz
 * `filter_map` sobre `partner_category_ids` — categoria apagada some da lista
 * sem levantar, então ela pode chegar menor do que os ids, ou vazia.
 */
type PartnerLike = { partner_categories?: (PartnerCategory | null)[] | null }

export type CategoryOption = { value: string; label: string }

/**
 * Acumula sobre as opções já conhecidas em vez de recalcular do zero.
 *
 * Isso importa porque a lista de parceiros encolhe quando um filtro é aplicado:
 * derivando só do que está carregado, escolher uma categoria deixaria apenas ela
 * no seletor, e não haveria como voltar. A carga inicial é sem filtro, então ela
 * semeia todas.
 *
 * @param partners parceiros carregados agora
 * @param previous opções já conhecidas, de cargas anteriores
 */
export const deriveCategoryOptions = (
  partners: PartnerLike[] | null | undefined,
  previous: CategoryOption[] = []
): CategoryOption[] => {
  const byId = new Map<string, CategoryOption>(
    previous.map(option => [option.value, option])
  )

  // Duas fontes de repetição agora, não uma: a mesma categoria aparece na
  // lista de vários Parceiros, e um Parceiro traz várias categorias. O `Map`
  // por id resolve as duas — uma opção por categoria, nunca uma por ocorrência.
  ;(partners ?? []).forEach(partner => {
    ;(partner?.partner_categories ?? []).forEach(category => {
      if (!category?.id || !category.name) return

      const value = String(category.id)

      if (!byId.has(value)) byId.set(value, { value, label: category.name })
    })
  })

  // `Array.from` e não spread: o alvo do tsconfig é anterior a es2015 e
  // desestruturar um iterador exigiria `downlevelIteration`.
  return Array.from(byId.values()).sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'))
}
